import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as log from '../../util/log';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { createImageVariantConfig } from '../../util/sdkLoader';

// ================ Async Thunks ================ //

const fetchBrowseListingsPayloadCreator = async (arg, thunkAPI) => {
  const { extra: sdk, rejectWithValue, dispatch } = thunkAPI;

  return sdk.listings
    .query({
      perPage: 8,
      page: 1,
      minStock: 1,
      stockMode: 'match-undefined',
      include: ['images', 'author'],
      'fields.listing': [
        'title',
        'geolocation',
        'price',
        'deleted',
        'state',
        'createdAt',
        'publicData.listingType',
        'publicData.transactionProcessAlias',
        'publicData.unitType',
        'publicData.cardStyle',
        'publicData.condition',
        'publicData.location',
      ],
      'fields.image': [
        'variants.listing-card',
        'variants.listing-card-2x',
        'variants.scaled-small',
        'variants.scaled-medium',
        'variants.scaled-xlarge',
      ],
      ...createImageVariantConfig('listing-card', 400, 1),
      ...createImageVariantConfig('listing-card-2x', 800, 1),
      'limit.images': 1,
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      const listingIds = response.data.data.map(l => l.id);
      return { listingIds };
    })
    .catch(error => {
      log.error(error, 'browse-page-fetch-listings-failed');
      return rejectWithValue(storableError(error));
    });
};

export const fetchBrowseListings = createAsyncThunk(
  'BrowsePage/fetchBrowseListings',
  fetchBrowseListingsPayloadCreator
);

// Sparse query: minimum fields for map pin + hover card.
// One image at the smallest variant only; no author, no publicData bloat.
const MAP_QUERY = {
  minStock: 1,
  stockMode: 'match-undefined',
  perPage: 100,
  include: ['images'],
  'fields.listing': ['title', 'geolocation', 'price'],
  'fields.image': ['variants.listing-card'],
  'limit.images': 1,
};

const fetchMapListingsPayloadCreator = async (arg, thunkAPI) => {
  const { extra: sdk, rejectWithValue } = thunkAPI;
  const { keywords, conditions, priceMin, priceMax, origin, distance } = arg || {};
  const keywordsMaybe = keywords?.trim() ? { keywords: keywords.trim() } : {};
  const conditionMaybe =
    conditions && conditions.length > 0 ? { pub_condition: `has_any:${conditions.join(',')}` } : {};
  const priceMaybe = (() => {
    const min = priceMin != null ? Math.round(priceMin * 100) : null;
    const max = priceMax != null ? Math.round(priceMax * 100) + 1 : null;
    if (min != null && max != null) return { price: `${min},${max}` };
    if (min != null) return { price: `${min},` };
    if (max != null) return { price: `0,${max}` };
    return {};
  })();
  const originMaybe = origin ? { origin } : {};
  const distanceMaybe = origin && distance ? { distance } : {};

  try {
    const raw = [];
    const imageById = {};
    const query = {
      ...MAP_QUERY,
      ...keywordsMaybe,
      ...conditionMaybe,
      ...priceMaybe,
      ...originMaybe,
      ...distanceMaybe,
    };

    const collectPage = response => {
      raw.push(...response.data.data);
      (response.data.included || []).forEach(inc => {
        if (inc.type === 'image') imageById[inc.id.uuid] = inc;
      });
    };

    const first = await sdk.listings.query({ ...query, page: 1 });
    collectPage(first);
    const { totalPages } = first.data.meta;

    if (totalPages > 1) {
      const remaining = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
      for (let i = 0; i < remaining.length; i += 5) {
        const batch = remaining.slice(i, i + 5);
        const pages = await Promise.all(
          batch.map(page => sdk.listings.query({ ...query, page }))
        );
        pages.forEach(collectPage);
      }
    }

    // Extract only what MapView needs: flat primitives, no nested objects
    const pins = raw
      .filter(l => l.attributes.geolocation)
      .map(l => {
        const { title = '', price, geolocation } = l.attributes;
        const imgId = l.relationships?.images?.data?.[0]?.id?.uuid;
        const imgVars = imgId ? imageById[imgId]?.attributes?.variants : null;
        return {
          id: l.id.uuid,
          lat: geolocation.lat,
          lng: geolocation.lng,
          title,
          priceAmount: price?.amount ?? null,
          priceCurrency: price?.currency || 'USD',
          imgUrl: imgVars?.['listing-card']?.url || '',
        };
      });

    return { pins };
  } catch (error) {
    log.error(error, 'browse-page-fetch-map-listings-failed');
    return rejectWithValue(storableError(error));
  }
};

export const fetchMapListings = createAsyncThunk(
  'BrowsePage/fetchMapListings',
  fetchMapListingsPayloadCreator
);

// ================ Slice ================ //

const browsePageSlice = createSlice({
  name: 'BrowsePage',
  initialState: {
    featuredListingIds: [],
    mapPins: [],
    fetchInProgress: false,
    mapFetchInProgress: false,
    fetchError: null,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchBrowseListings.pending, state => {
        state.fetchInProgress = true;
        state.fetchError = null;
      })
      .addCase(fetchBrowseListings.fulfilled, (state, action) => {
        state.fetchInProgress = false;
        state.featuredListingIds = action.payload.listingIds;
      })
      .addCase(fetchBrowseListings.rejected, (state, action) => {
        state.fetchInProgress = false;
        state.fetchError = action.payload;
      })
      .addCase(fetchMapListings.pending, state => {
        state.mapFetchInProgress = true;
      })
      .addCase(fetchMapListings.fulfilled, (state, action) => {
        state.mapFetchInProgress = false;
        state.mapPins = action.payload.pins;
      })
      .addCase(fetchMapListings.rejected, state => {
        state.mapFetchInProgress = false;
      });
  },
});

export default browsePageSlice.reducer;

// ================ loadData ================ //

export const loadData = () => (dispatch, getState, sdk) => {
  return dispatch(fetchBrowseListings());
};
